import { formatNumber, shortDay } from "../../lib/format";

export interface BarPoint {
  label: string;
  value: number;
}

/**
 * Daily series as columns: one accent series, a labelled ceiling and a hairline baseline.
 * No charting dependency — at this scale the SVG is smaller than the library.
 */
export function BarChart({
  points,
  height = 132,
  format = formatNumber,
}: {
  points: BarPoint[];
  height?: number;
  format?: (value: number) => string;
}) {
  const max = Math.max(1, ...points.map((point) => point.value));
  const step = 100 / Math.max(points.length, 1);
  const plot = height - 8;

  return (
    <div>
      <div className="text-micro text-faint mb-1.5 flex items-baseline justify-between font-semibold uppercase">
        <span>peak {format(max)}</span>
        <span className="tabular">{points.length} days</span>
      </div>
      <div className="relative">
        <svg
          viewBox={`0 0 100 ${height}`}
          preserveAspectRatio="none"
          className="w-full"
          style={{ height }}
          role="img"
          aria-label={`Daily values, peak ${format(max)}`}
        >
          <line
            x1="0"
            x2="100"
            y1={height - 1}
            y2={height - 1}
            className="stroke-line"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
          {points.map((point, index) => {
            const barHeight = Math.max(2, (point.value / max) * plot);
            return (
              <rect
                key={point.label}
                x={index * step + step * 0.2}
                y={height - 1 - barHeight}
                width={step * 0.6}
                height={barHeight}
                rx={0.7}
                /* Days with no traffic keep a 2px floor tick so gaps read as zero, not as a gap. */
                className={
                  point.value === 0
                    ? "fill-line"
                    : "fill-accent/55 hover:fill-accent transition-[fill] duration-200"
                }
              >
                <title>{`${point.label}: ${format(point.value)}`}</title>
              </rect>
            );
          })}
        </svg>
      </div>
      <div className="text-caption text-faint tabular mt-1.5 flex justify-between">
        <span>{shortDay(points[0]?.label)}</span>
        <span>{shortDay(points[points.length - 1]?.label)}</span>
      </div>
    </div>
  );
}
